import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, UsersRound } from 'lucide-react';
import {
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  type AppUser,
  type Organization,
  type Page,
  type Role,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatRelative } from '../lib/format';
import { useSession } from '../lib/session';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

/** Most authority first, so the picker reads down the ladder. */
const ASSIGNABLE_ROLES: Role[] = [
  'org_admin',
  'org_engineer',
  'org_operator',
  'org_viewer',
  'org_technician',
];

export function UsersPage() {
  const { session, activeOrg } = useSession();
  const queryClient = useQueryClient();
  const isSuperadmin = session?.user.role === 'superadmin';

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    email: '',
    name: '',
    phone: '',
    password: '',
    role: 'org_operator' as Role,
    orgId: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  // Editing one existing user (name / role / optional password reset).
  const [editing, setEditing] = useState<AppUser | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'org_operator' as Role,
    password: '',
  });
  const [editError, setEditError] = useState<string | null>(null);

  function startEdit(user: AppUser) {
    setEditing(user);
    setEditForm({ name: user.name, email: user.email, phone: user.phone ?? '', role: user.role, password: '' });
    setEditError(null);
    setShowForm(false);
  }

  const usersQuery = useQuery({
    queryKey: ['users', activeOrg?.id ?? 'all'],
    queryFn: () => api.get<Page<AppUser>>('/users'),
  });

  const orgsQuery = useQuery({
    queryKey: ['orgs'],
    queryFn: () => api.get<Page<Organization>>('/orgs'),
    enabled: isSuperadmin,
  });

  const createUser = useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<AppUser>('/users', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowForm(false);
      setForm({ email: '', name: '', phone: '', password: '', role: 'org_operator', orgId: '' });
      setFormError(null);
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'Could not create user'),
  });

  const updateUser = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.patch<AppUser & { notes?: string[] }>(`/users/${id}`, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const deleteUser = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/users/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  /**
   * Take two-step sign-in off somebody's account — the "they lost their phone"
   * button.
   *
   * Confirmed rather than one-click because it genuinely weakens that account,
   * and it reports the outcome as a sentence: an administrator who presses this
   * needs to know the person can now sign in with a password alone, so they can
   * tell them to set it up again.
   */
  const resetMfa = useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; email: string }>(`/users/${id}/mfa/reset`),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      alert(
        `Two-step sign-in removed from ${result.email}. They sign in with their password alone ` +
          `until they switch it on again.`,
      );
    },
    onError: (err) => alert(err instanceof Error ? err.message : 'Could not reset it.'),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const payload: Record<string, unknown> = {
      email: form.email.trim().toLowerCase(),
      name: form.name.trim(),
      password: form.password,
      role: form.role,
      phone: form.phone.trim() || null,
    };
    if (isSuperadmin) {
      payload.orgId = form.orgId || activeOrg?.id || null;
    }
    createUser.mutate(payload);
  }

  function onEditSubmit(event: FormEvent) {
    event.preventDefault();
    if (!editing) return;
    const patch: Record<string, unknown> = { name: editForm.name.trim(), role: editForm.role };
    // Blank keeps the current password.
    if (editForm.password) patch.password = editForm.password;
    // Email and phone are a platform administrator's to set (2026-09-17); an
    // org admin's form shows them read-only, so it never posts them.
    if (isSuperadmin) {
      const email = editForm.email.trim().toLowerCase();
      if (email !== editing.email) patch.email = email;
      const phone = editForm.phone.trim();
      if (phone !== (editing.phone ?? '')) patch.phone = phone || null;
    }
    updateUser.mutate(
      { id: editing.id, patch },
      {
        onSuccess: (result) => {
          setEditing(null);
          setEditError(null);
          // The API says when the change cost the person their second factor.
          if (result.notes?.length) alert(result.notes.join(' '));
        },
        onError: (err) =>
          setEditError(err instanceof Error ? err.message : 'Could not update the user'),
      },
    );
  }

  const roleChoices: Role[] = isSuperadmin ? ['superadmin', ...ASSIGNABLE_ROLES] : ASSIGNABLE_ROLES;

  return (
    <>
      <PageHeader
        title="Users"
        description={
          isSuperadmin && !activeOrg
            ? 'All users across every organization.'
            : `Users in ${activeOrg?.name ?? 'your organization'}.`
        }
        actions={
          <Button
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => setShowForm((v) => !v)}
          >
            New user
          </Button>
        }
      />

      {showForm && (
        <Card title="New user" className="mb-5">
          <form onSubmit={onSubmit} className="grid gap-4 p-5 md:grid-cols-2 xl:grid-cols-3">
            <Field label="Name">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Field>
            <Field label="Email">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </Field>
            <Field label="Phone" hint="Optional. International format, e.g. +254712345678.">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field label="Password" hint="Minimum 10 characters">
              <Input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                minLength={10}
                required
              />
            </Field>
            <Field label="Role" hint={ROLE_DESCRIPTIONS[form.role]}>
              <select
                className="input"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
              >
                {roleChoices.map((role) => (
                  <option key={role} value={role} title={ROLE_DESCRIPTIONS[role]}>
                    {ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
            </Field>
            {isSuperadmin && form.role !== 'superadmin' && (
              <Field label="Organization">
                <select
                  className="input"
                  value={form.orgId || activeOrg?.id || ''}
                  onChange={(e) => setForm({ ...form, orgId: e.target.value })}
                  required
                >
                  <option value="">Select…</option>
                  {orgsQuery.data?.items.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <div className="flex items-end">
              <Button type="submit" variant="primary" loading={createUser.isPending}>
                Create
              </Button>
            </div>
            {formError && (
              <div className="md:col-span-2 xl:col-span-3">
                <ErrorNotice error={new Error(formError)} />
              </div>
            )}
          </form>
        </Card>
      )}

      {editing && (
        <Card title={`Edit ${editing.name}`} className="mb-5">
          <form onSubmit={onEditSubmit} className="grid gap-4 p-5 md:grid-cols-2 xl:grid-cols-3">
            <Field label="Name">
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                autoFocus
              />
            </Field>
            <Field
              label="Email"
              hint={
                isSuperadmin
                  ? 'Their username. Changing it clears its proof and switches off two-step sign-in that used it.'
                  : 'Changed by the person themselves, from their profile, with a confirmation code.'
              }
            >
              <Input
                type="email"
                value={editForm.email}
                disabled={!isSuperadmin}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required
              />
            </Field>
            <Field
              label="Phone"
              hint={
                isSuperadmin
                  ? 'International format, e.g. +254712345678. Blank removes it.'
                  : 'Changed by the person themselves, from their profile.'
              }
            >
              <Input
                value={editForm.phone}
                disabled={!isSuperadmin}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </Field>
            <Field
              label="Role"
              hint={
                editing.id === session?.user.id
                  ? 'You cannot change your own role'
                  : ROLE_DESCRIPTIONS[editForm.role]
              }
            >
              <select
                className="input"
                value={editForm.role}
                disabled={editing.id === session?.user.id}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value as Role })}
              >
                {roleChoices.map((role) => (
                  <option key={role} value={role} title={ROLE_DESCRIPTIONS[role]}>
                    {ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="New password" hint="Leave blank to keep the current one">
              <Input
                type="text"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                minLength={10}
                placeholder="•••••• (unchanged)"
              />
            </Field>
            <div className="flex items-end gap-2">
              <Button type="submit" variant="primary" loading={updateUser.isPending}>
                Save
              </Button>
              <Button type="button" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
            {editError && (
              <div className="md:col-span-2 xl:col-span-3">
                <ErrorNotice error={new Error(editError)} />
              </div>
            )}
          </form>
        </Card>
      )}

      <Card>
        {usersQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : usersQuery.error ? (
          <div className="p-5">
            <ErrorNotice error={usersQuery.error} />
          </div>
        ) : (usersQuery.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            icon={<UsersRound className="size-8" />}
            title="No users"
            description="Invite operators and technicians so they can work on this organization's ONUs."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">Name</th>
              <th className="th">Email</th>
              <th className="th">Role</th>
              {isSuperadmin && <th className="th">Organization</th>}
              <th className="th">Status</th>
              <th className="th">Last login</th>
              <th className="th" />
            </THead>
            <TBody>
              {usersQuery.data?.items.map((user) => {
                const isSelf = user.id === session?.user.id;
                return (
                  <TRow key={user.id}>
                    <td className="td font-medium">
                      {user.name}
                      {isSelf && <span className="ml-2 text-xs text-faint">(you)</span>}
                    </td>
                    <td className="td text-muted">{user.email}</td>
                    <td className="td">
                      <Pill tone={user.role === 'superadmin' ? 'primary' : 'neutral'}>
                        {ROLE_LABELS[user.role]}
                      </Pill>
                    </td>
                    {isSuperadmin && <td className="td text-muted">{user.orgName ?? '—'}</td>}
                    <td className="td">
                      <Pill state tone={user.status === 'active' ? 'success' : 'warn'} dot>
                        {user.status}
                      </Pill>
                    </td>
                    <td className="td text-muted">{formatRelative(user.lastLoginAt)}</td>
                    <td className="td">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          icon={<Pencil className="size-3.5" />}
                          onClick={() => startEdit(user)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          disabled={isSelf}
                          onClick={() =>
                            updateUser.mutate({
                              id: user.id,
                              patch: { status: user.status === 'active' ? 'disabled' : 'active' },
                            })
                          }
                        >
                          {user.status === 'active' ? 'Disable' : 'Enable'}
                        </Button>
                        <Button
                          size="sm"
                          loading={resetMfa.isPending}
                          onClick={() => {
                            if (
                              confirm(
                                `Remove two-step sign-in from ${user.email}? They will sign in ` +
                                  `with their password alone until they set it up again.`,
                              )
                            ) {
                              resetMfa.mutate(user.id);
                            }
                          }}
                        >
                          Reset 2FA
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={isSelf}
                          icon={<Trash2 className="size-3.5" />}
                          onClick={() => {
                            if (confirm(`Delete ${user.email}? This cannot be undone.`)) {
                              deleteUser.mutate(user.id);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </TRow>
                );
              })}
            </TBody>
          </Table>
        )}
      </Card>
    </>
  );
}
